import { useInfiniteQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { stringify } from 'query-string';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';

import useSearchInput from '@dashboard/hooks/useSearchInput';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';

import { fetchInfinite } from '@dashboard/services/request';

import BottomScrollListener from '@dashboard/components/BottomScrollListener';
import { FormElement, Label, TextInput } from '@dashboard/components/Form';
import Link from '@dashboard/components/Link';
import Spinner from '@dashboard/components/Spinner';

import Table from '../components/Table';

const HelpCenterSearchIntents = () => {
  const { token } = useAuth();
  const history = useHistory();
  const { searchInput, setSearchInput, search } = useSearchInput();

  const query = useUrlQuery();
  const [sortBy, setSortBy] = useState(query.sortBy || 'createdAt');
  const [sortDirection, setSortDirection] = useState(query.sortDirection || -1);
  const [limit] = useState(20);

  useEffect(() => {
    history.replace({
      search: stringify({
        sortBy,
        sortDirection,
        search,
        limit,
      }),
    });
  }, [sortBy, sortDirection, search, limit]);

  const { data, fetchNextPage, isLoading, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['help-center-searches', sortBy, sortDirection, search, limit],
    queryFn: async ({ pageParam }) =>
      fetchInfinite(
        `/admin/help-center-search-intents?${stringify({
          sort: JSON.stringify({
            [sortBy]: sortDirection,
          }),
          search,
          limit: limit,
          skip: pageParam * limit,
        })}`,
        token,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;

      return allPages.length;
    },
  });

  function onSort(newSortBy) {
    const oppositeSortDirection = sortDirection * -1;

    setSortDirection(sortBy === newSortBy ? oppositeSortDirection : -1);
    setSortBy(newSortBy);
  }

  return (
    <div className="pr-4">
      <div className="flex items-end justify-end gap-x-2">
        <FormElement className="!mb-0">
          <Label htmlFor="search">Search</Label>
          <div className="w-64">
            <TextInput
              border
              name="search"
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Search by name, email, ID"
              value={searchInput}
            />
          </div>
        </FormElement>
      </div>

      <Table.Table>
        <Table.Head>
          <Table.Th onSort={onSort} sortKey="query" sortBy={sortBy} sortDirection={sortDirection}>
            Search query
          </Table.Th>

          <Table.Th onSort={onSort} sortKey="seller" sortBy={sortBy} sortDirection={sortDirection}>
            Merchant
          </Table.Th>

          <Table.Th
            onSort={onSort}
            sortKey="createdAt"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Date
          </Table.Th>
        </Table.Head>

        {!isLoading && (
          <BottomScrollListener onBottom={fetchNextPage} as="tbody">
            {data.pages.map(page =>
              page.data.map(searchIntent => (
                <Table.Row key={searchIntent.id}>
                  <Table.Td>{searchIntent.query}</Table.Td>
                  <Table.Td>
                    {searchIntent.seller && (
                      <Link href={`/admin/merchants/${searchIntent.seller._id}`}>
                        {searchIntent.seller.displayName || searchIntent.seller.name}
                      </Link>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {format(new Date(searchIntent.createdAt), 'd MMM, yyyy, HH:mm')}
                  </Table.Td>
                </Table.Row>
              )),
            )}
          </BottomScrollListener>
        )}
      </Table.Table>

      {(isFetchingNextPage || isLoading) && <Spinner />}
    </div>
  );
};

export default HelpCenterSearchIntents;
